import type { ComponentType } from 'react';
import {
  DomainPreferences,
  NonCoreDashboardPageToChartSettingKeys,
} from '../../../api/types';
import { Languages } from '../../../constants';
import { StoryboardDateUtils } from '../../../settings/dashboard-configs/utils';
import { DataFieldWithDataType } from '../../../types';
import { StoryboardItemConfig } from '../storyboard-components/common/types';
import { TimeSliderGranularity, TimeSliderTypes } from '../timeslider/types';

type GridColSpan = number;

type GridRowSpan = number;

type GridCols = number;

type GridRows = number;

type GridColStart = number;

type GridColEnd = number;

type GridRowStart = number;

type GridRowEnd = number;

export type LanguageString = Record<Languages, string>;
export interface ChartGridConfig {
  gridColSpan?: GridColSpan;
  gridRowSpan?: GridRowSpan;
  gridColStart?: GridColStart;
  gridColEnd?: GridColEnd;
  gridRowStart?: GridRowStart;
  gridRowEnd?: GridRowEnd;
}

export interface StoryboardComponentDependencies {
  domainPreferences: DomainPreferences;
  storyboardDateUtils: StoryboardDateUtils;
}

type PageConfigBase = {
  pageId: string;
  dashboardId: CustomDashboardId;
  pageNumber: number;
  enabled: boolean;
  title: LanguageString;
  link?: string;
  // TODO: Not sure about 'link' above. The behaviour I have now is that if link is provided,
  // it will use that link, otherwise, it will use the /custom/... link
  // The former will be used for pages which use the CustomDashboard component but the config
  // right now is stored in the legacy dashboard object, and the url is also the legacy dashboard url
  // without the 'custom' prefix
  gridCols?: GridCols;
  gridRows?: GridRows;
  header?: string; // markdown
  footer?: string; // markdown
  pageOptions: DashboardPageOptions;
};

export type PageConfigComponentsGetter = (
  componentDependencies: StoryboardComponentDependencies
) => StoryboardItemConfig[];

export type PageConfig =
  | (PageConfigBase & {
      configType: 'componentConfigs';
      components: PageConfigComponentsGetter;
    })
  | (PageConfigBase & {
      configType: 'pageComponent';
      pageComponent: ComponentType;
    });

type EnabledOption = {
  enabled?: boolean;
};

export interface TimeSliderDisabledOptions {
  timeSliderEnabled: false;
  referenceDate?: string;
  /**
   * When true, anchor the page to the warehouse's latest available month
   * (same source as the PBS timeslider's initial value, i.e.
   * `availableTimeRanges[Granularity.MONTH].max`). Useful for slider-less
   * pages that must reflect the freshest loaded data instead of "today".
   * Ignored when `referenceDate` is provided.
   */
  useLatestWarehouseMonth?: boolean;
}

export interface TimeSliderEnabledOptions {
  timeSliderEnabled: true;
  timeSliderType: TimeSliderTypes;
  /** Override the default granularity for the time slider (e.g. lock to financial year). */
  defaultGranularity?: TimeSliderGranularity;
}

export type DashboardPageTimeOptions =
  | TimeSliderDisabledOptions
  | TimeSliderEnabledOptions;

export type DashboardPageOptions = {
  dataView?: EnabledOption & {};
  filterTray?: EnabledOption & {
    myFilters?: EnabledOption;
    allowedFilterFields?: DataFieldWithDataType[];
  };
  segmentationLevel1?: EnabledOption & {};
  segmentationLevel2?: EnabledOption & {};
  benchmark?: EnabledOption & {};
  forecast?: EnabledOption & {};
  displayOptions?: EnabledOption & {};
  timeOptions?: DashboardPageTimeOptions;
};

type CustomDashboardId = string;

export interface DashboardOptions {
  allowedFilterFields?: DataFieldWithDataType[];
}
export interface DashboardConfig {
  title: LanguageString;
  id: CustomDashboardId;
  enabled: boolean;
  pages: PageConfig[];
  dashboardOptions?: DashboardOptions;
  qaOnly?: boolean;
  superAdminOnly?: boolean;
}

export interface PageHandle {
  selected: PageConfig;
  pages: PageConfig[];
  setter: (page: PageConfig) => void;
}

export type DashboardId = Dashboards;
export type DashboardPageId = DashboardPages;

export interface DashboardIdAndPageId {
  dashboardId: DashboardId;
  pageId: DashboardPageId;
}

export interface NonCoreDashboardIdAndPageId {
  dashboardId: DashboardId;
  pageId: NonCoreDashboardPageToChartSettingKeys;
}

type Description = string;
type DescriptionKey = string;
type ImagePath = string;
type Link = string;
type Name = string;
type NameKey = string;

export interface DashboardPage {
  description: Description;
  descriptionKey: DescriptionKey;
  enabled: boolean;
  imagePath: ImagePath;
  link: Link;
  name: Name;
  nameKey: NameKey;
  pageId: DashboardPageId;
  pageNumber: number;
  dashboardId: DashboardId;
  pageComponent?: any;
  pageOptions?: { disableDataView?: boolean };
}

export interface Dashboard {
  description: Description;
  descriptionKey: DescriptionKey;
  enabled: boolean;
  id: DashboardId;
  imagePath: ImagePath;
  link: Link;
  name: Name;
  nameKey: NameKey;
  pages: DashboardPage[];
}

export interface DashboardList {
  dashboards: Dashboard[];
}

export enum Dashboards {
  // Note: the actual values should be ids as in the backend config for these dashboards
  ATTRITION = 'attrition',
  EVALUATION = 'evaluation',
  GENDER_DIVERSITY = 'genderDiversity',
  JOINERS_AND_LEAVERS = 'joinersAndLeavers',
  MEDIAN_BASE = 'medianBase',
  MOVEMENTS = 'movement',
  PAYROLL = 'payroll',
  PEOPLE_BALANCE_SHEET = 'peopleBalanceSheet',
  RECRUITMENT_BALANCE_SHEET = 'recruitmentBalanceSheet',
  PEOPLE_SNAPSHOT = 'peopleSnapshot',
  RECRUITMENT_FUNNEL = 'recruitmentFunnel',
  RECRUITMENT_OVERVIEW = 'recruitment',
  SURVEY = 'survey',
  WORK_HOURS_AND_OT = 'workHoursAndOt',
}

// The ids should not be changed, ideally they should come from the backend
// so we can ensure that they're the same
export enum DashboardPages {
  ATTRITION_OVERVIEW = 'attrition-overview',
  GENDER_DIVERSITY_DEMOGRAPHICS = 'gender-diversity-demographics',
  GENDER_DIVERSITY_ATTRITION = 'gender-diversity-attrition',
  JOINERS_AND_LEAVERS_OVERVIEW = 'joiners-and-leavers-overview',
  COMPANY_STRUCTURE_ORGANIZATIONS = 'company-structure-organizations',
  COMPANY_STRUCTURE_LOCATIONS = 'company-structure-locations',
  COMPANY_STRUCTURE_JOB_GRADES = 'company-structure-job-grades',
  COMPANY_STRUCTURE_FUNCTIONS = 'company-structure-functions',
  COMPANY_STRUCTURE_EMPLOYMENT_TYPES = 'company-structure-employment-types',
  MEDIAN_BASE_OVERVIEW = 'median-base-overview',
  MEDIAN_BASE_KEY_METRICS = 'median-base-key-metrics',
  ONA_TRAINS_GRAPH = 'ona-trains-graph',
  RECRUITMENT_FUNNEL_OVERVIEW = 'recruitment-funnel-overview',
  PAYROLL_COST_TO_COMPANY = 'payroll-cost-to-company',
  PAYROLL_COST_TO_EMPLOYEE = 'payroll-cost-to-employee',
  SNAPSHOT_HEADCOUNT = 'snapshot-headcount',
  SNAPSHOT_KPIS = 'snapshot-kpis',
  SNAPSHOT_ORG_METRICS = 'snapshot-org-metrics',
  RBS_OVERVIEW = 'rbs-overview',
  RBS_METRICS_OVER_TIME = 'rbs-metrics-over-time',
  RBS_ISO_DEFINITIONS = 'rbs-iso-definitions',
  RELATIONSHIP_TO_CLIENTS_OVERVIEW = 'relationship-to-clients-overview',
  RELATIONSHIP_TO_CLIENTS_SINGLE_CLIENT = 'relationship-to-clients-single-client',
  RELATIONSHIP_TO_CLIENTS_OVER_TIME = 'relationship-to-clients-over-time',
  WORKHOURS_AND_OT_WORKHOURS = 'workhours-and-ot-workhours',
  WORKHOURS_AND_OT_OVERTIME = 'workhours-and-ot-overtime',
  PBS_OVERVIEW = 'pbs-overview',
  PBS_METRICS_OVER_TIME = 'pbs-metrics-over-time',
  MOVEMENT_OVERVIEW = 'movement-overview',
  MOVEMENT_NO_MOVEMENTS = 'movement-no-movements',
  RECRUITMENT_OVERVIEW = 'recruitment-overview',
  RECRUITMENT_ACTIVITIES = 'recruitment-activities',
  ONA_BS_OVERVIEW = 'ona-bs-overview',
  ONA_METRICS_OVER_TIME = 'ona-metrics-over-time',
  SURVEY_OVERALL_QUESTIONS = 'survey-overall-questions',
  SURVEY_SENTIMENT = 'survey-sentiment',
  ONA_GRAPH = 'ona-graph',
  ONA_GRAVITY = 'ona-gravity',
  EVALUATION_OVERVIEW = 'evaluation-overview',
  MULTICYCLE_EVALUATION = 'multicycle-evaluation',
  ATTRITION_RISK_OVERVIEW = 'attrition-risk-overview',
  ATTRITION_RISK_EMPLOYEES = 'attrition-risk-employees',
  ATTRITION_RISK_MANAGERS = 'attrition-risk-managers',
}
